import type { LucideIcon } from 'lucide-react';
import { LayoutPanelLeft, MessageCircle } from 'lucide-react';

export type LivingWebsiteTierId = 'living-website-lite' | 'living-website-pro';

export interface LivingWebsiteTier {
  id: LivingWebsiteTierId;
  name: string;
  tagline: string;
  setupLabel: string;
  setupCents: number;
  monthlyLabel: string;
  monthlyCents: number;
  description: string;
  features: string[];
  highlighted?: boolean;
}

export const LIVING_WEBSITE_TIERS: LivingWebsiteTier[] = [
  {
    id: 'living-website-lite',
    name: 'Lite',
    tagline: 'Avatar on your site',
    setupLabel: '$3,500',
    setupCents: 350000,
    monthlyLabel: '$299',
    monthlyCents: 29900,
    description:
      'A photorealistic founder or brand avatar with curated knowledge — perfect for creators and small businesses.',
    features: [
      'Role Model AI Expert embed + avatar setup',
      'Up to ~15 knowledge topics',
      'Bubble or launcher on your site',
      'Knowledge base writing & launch support',
      'Monthly care & minor content updates',
    ],
  },
  {
    id: 'living-website-pro',
    name: 'Pro',
    tagline: 'Like selflabs.xyz',
    setupLabel: '$12,000',
    setupCents: 1200000,
    monthlyLabel: '$999',
    monthlyCents: 99900,
    description:
      'Full site-aware living website — visitors talk to your avatar while real pages open beside them, in sync.',
    features: [
      'Everything in Lite',
      'Split-panel page preview (homepage experience)',
      'Site map, topic registry & page actions',
      'Explore chips + Manifest host-tools integration',
      'Up to ~30+ topics, ongoing KB & integration care',
    ],
    highlighted: true,
  },
];

export const LIVING_WEBSITE_ENTERPRISE_BUDGET_RANGES = [
  '$25,000–$50,000',
  '$50,000–$100,000',
  '$100,000–$250,000',
  '$250,000+',
  'Not determined',
] as const;

export const LIVING_WEBSITE_ENTERPRISE_COMPANY_SIZES = [
  '1–50',
  '51–200',
  '201–1,000',
  '1,001–5,000',
  '5,000+',
] as const;

export interface LivingWebsiteEnterprise {
  id: 'living-website-enterprise';
  name: string;
  tagline: string;
  pricingLabel: string;
  description: string;
  headline: string;
  body: string;
  subcopy: string;
  features: string[];
}

export const LIVING_WEBSITE_ENTERPRISE: LivingWebsiteEnterprise = {
  id: 'living-website-enterprise',
  name: 'Enterprise',
  tagline: 'Custom pricing — Contact us',
  pricingLabel: 'Custom deployment and licensing',
  description: 'For companies that need a more advanced deployment.',
  headline: 'Enterprise Living Websites',
  body: 'Turn your entire digital customer journey into a conversation.',
  subcopy:
    'Deploy branded AI advisors across products, divisions, locations, and markets. Enterprise Living Websites can qualify visitors, recommend the right solution, navigate complex websites, connect with business systems, and move customers toward the next action—all through one natural conversation. Built for enterprise organizations requiring custom integrations, high-volume infrastructure, advanced security, analytics, localization, and dedicated support.',
  features: [
    'Multiple products, divisions, brands, or locations',
    'Multiple avatars',
    'Multilingual experiences',
    'High-traffic infrastructure',
    'Custom CRM, inventory, catalog, API, or account integrations',
    'Advanced analytics and conversion tracking',
    'Security and compliance review',
    'Staging and production environments',
    'Custom AI workflows',
    'Regional content and product availability',
    'Dedicated support',
    'Service-level agreements',
    'Pilot programs and phased rollouts',
    'White-label or dealer-network deployments',
  ],
};

export const LIVING_WEBSITE_HIGHLIGHTS: {
  icon: LucideIcon;
  title: string;
  text: string;
}[] = [
  {
    icon: MessageCircle,
    title: 'Talk instead of click',
    text: 'Visitors ask your avatar to explain products, show pages, and guide them — like talking to Milan on this site.',
  },
  {
    icon: LayoutPanelLeft,
    title: 'Pages update in sync',
    text: 'Pro tier opens your real site routes in a split panel while the conversation continues — no static brochure sites.',
  },
];

export function formatUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
